import { useState, useEffect, useRef } from 'react';
import { useScore } from '../../../context/ScoreContext';
import { MISSION_TASK_KEY } from '../../../data/scheduleData';

const PARTICLE_COUNT = 40;

interface Particle {
  id: number;
  angle: number;
  distance: number;
  size: number;
  color: string;
  delay: number;
  duration: number;
  shape: 'circle' | 'heart' | 'star';
}

const COLORS = [
  '#c06b9a', '#e8a0cc', '#f5c0de', '#ff88bb',
  '#d4a843', '#f5d080', '#ffffff', '#ffd6ee',
  '#b06090', '#7ec8a0', '#ffb3d9',
];

function generateParticles(): Particle[] {
  const shapes: Particle['shape'][] = ['circle', 'heart', 'star'];
  return Array.from({ length: PARTICLE_COUNT }, (_, i) => ({
    id: i,
    angle: (i / PARTICLE_COUNT) * 360 + Math.random() * 18 - 9,
    distance: 100 + Math.random() * 220,
    size: 5 + Math.random() * 10,
    color: COLORS[i % COLORS.length],
    delay: Math.random() * 0.5,
    duration: 0.9 + Math.random() * 0.7,
    shape: shapes[i % 3],
  }));
}

export default function SecretAngelEffect() {
  const { scheduleState, isLoading } = useScore();
  const missionUnlocked = !!scheduleState.unlockedTasks[MISSION_TASK_KEY];

  const [visible, setVisible] = useState(false);
  const [phase, setPhase] = useState<'in' | 'show' | 'out'>('in');
  const [particles] = useState<Particle[]>(() => generateParticles());
  const prevRef = useRef<boolean>(false);
  const initializedRef = useRef(false);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const dismiss = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    setPhase('out');
    const t = setTimeout(() => { setVisible(false); setPhase('in'); }, 900);
    timersRef.current = [t];
  };

  useEffect(() => {
    if (isLoading) return;

    if (!initializedRef.current) {
      initializedRef.current = true;
      prevRef.current = missionUnlocked;
      return;
    }

    // Trigger when transitioning false → true
    if (!prevRef.current && missionUnlocked) {
      setVisible(true);
      setPhase('in');
      const t1 = setTimeout(() => setPhase('show'), 350);
      const t2 = setTimeout(() => setPhase('out'), 3500);
      const t3 = setTimeout(() => { setVisible(false); setPhase('in'); }, 4400);
      timersRef.current = [t1, t2, t3];
      prevRef.current = missionUnlocked;
      return () => { timersRef.current.forEach(clearTimeout); };
    }

    prevRef.current = missionUnlocked;
  }, [missionUnlocked, isLoading]);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden cursor-pointer"
      onClick={dismiss}
      style={{
        background: 'radial-gradient(ellipse at center, rgba(30,5,20,0.97) 0%, rgba(8,3,12,0.98) 100%)',
        opacity: phase === 'out' ? 0 : 1,
        transition: phase === 'out' ? 'opacity 0.9s ease-out' : phase === 'in' ? 'opacity 0.35s ease-in' : 'none',
      }}
    >
      {/* Radial rings — pink/purple */}
      {[1, 2, 3, 4, 5].map(i => (
        <div
          key={i}
          className="absolute rounded-full"
          style={{
            width: `${i * 160}px`,
            height: `${i * 160}px`,
            border: `1px solid rgba(192,107,154,${0.35 - i * 0.06})`,
            animation: `sa-ring-expand 2.2s ease-out ${i * 0.1}s both`,
          }}
        />
      ))}

      {/* Petal rays */}
      {Array.from({ length: 16 }, (_, i) => (
        <div
          key={i}
          className="absolute origin-center"
          style={{
            width: '1.5px',
            height: '0px',
            background: `linear-gradient(to top, rgba(192,107,154,0.85), transparent)`,
            transform: `rotate(${i * 22.5}deg)`,
            transformOrigin: 'bottom center',
            bottom: '50%',
            left: 'calc(50% - 0.75px)',
            animation: `sa-ray-shoot 1.4s ease-out 0.08s both`,
          }}
        />
      ))}

      {/* Particles */}
      {particles.map(p => (
        <div
          key={p.id}
          className="absolute"
          style={{
            width: `${p.size}px`,
            height: `${p.size}px`,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            animation: `sa-particle-fly-${p.id % 6} ${p.duration}s ease-out ${p.delay}s both`,
            '--px': `${Math.cos((p.angle * Math.PI) / 180) * p.distance}px`,
            '--py': `${Math.sin((p.angle * Math.PI) / 180) * p.distance}px`,
            fontSize: `${p.size}px`,
            color: p.color,
            textShadow: `0 0 ${p.size * 2}px ${p.color}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 1,
          } as React.CSSProperties}
        >
          {p.shape === 'heart' ? '♥' : p.shape === 'star' ? '✦' : (
            <div
              className="rounded-full w-full h-full"
              style={{ background: p.color, boxShadow: `0 0 ${p.size * 2}px ${p.color}` }}
            />
          )}
        </div>
      ))}

      {/* Central glow orb — pink */}
      <div
        className="absolute rounded-full"
        style={{
          width: '240px',
          height: '240px',
          background: 'radial-gradient(circle, rgba(192,107,154,0.45) 0%, rgba(192,107,154,0.12) 40%, transparent 70%)',
          filter: 'blur(24px)',
          animation: 'sa-orb-pulse 0.6s ease-out 0.1s both',
        }}
      />

      {/* Main text block */}
      <div className="relative flex flex-col items-center gap-3 z-10 px-6">

        {/* Main title */}
        <div style={{ animation: 'sa-text-slam 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.15s both' }}>
          <p
            className="font-black text-center select-none"
            style={{
              fontSize: 'clamp(36px, 9vw, 72px)',
              color: '#f5c0de',
              textShadow: '0 0 24px rgba(192,107,154,0.9), 0 0 70px rgba(192,107,154,0.4), 0 0 130px rgba(192,107,154,0.2)',
              fontFamily: 'Georgia, serif',
              letterSpacing: '0.12em',
              lineHeight: 1.2,
            }}
          >
            解鎖隱藏任務
          </p>
        </div>

        {/* Tap hint */}
        <div style={{ animation: 'sa-fade-up 0.5s ease-out 1.2s both' }}>
          <p
            className="text-center tracking-widest uppercase"
            style={{ fontSize: '10px', color: 'rgba(192,107,154,0.35)', fontFamily: "'Cinzel', serif", marginTop: '4px' }}
          >
            點擊畫面繼續
          </p>
        </div>

      </div>

      {/* Corner sparkles */}
      {[
        { pos: 'top-10 left-10', delay: '0.55s', icon: 'ri-heart-fill', color: '#e8a0cc' },
        { pos: 'top-10 right-10', delay: '0.65s', icon: 'ri-sparkling-2-fill', color: '#f5c0de' },
        { pos: 'bottom-10 left-10', delay: '0.75s', icon: 'ri-sparkling-fill', color: '#c06b9a' },
        { pos: 'bottom-10 right-10', delay: '0.85s', icon: 'ri-heart-fill', color: '#d4a843' },
      ].map(({ pos, delay, icon, color }) => (
        <div
          key={pos}
          className={`absolute ${pos}`}
          style={{ animation: `sa-corner-spark 1.1s ease-out ${delay} both` }}
        >
          <i className={`${icon} text-2xl`} style={{ color, textShadow: `0 0 14px ${color}` }} />
        </div>
      ))}

      <style>{`
        @keyframes sa-ring-expand {
          from { transform: scale(0); opacity: 0.9; }
          to { transform: scale(3.5); opacity: 0; }
        }
        @keyframes sa-ray-shoot {
          from { height: 0; opacity: 1; }
          50% { height: 200px; opacity: 0.85; }
          to { height: 300px; opacity: 0; }
        }
        @keyframes sa-orb-pulse {
          from { transform: scale(0); opacity: 0; }
          50% { transform: scale(2.2); opacity: 1; }
          to { transform: scale(1.3); opacity: 0.65; }
        }
        @keyframes sa-text-slam {
          from { transform: scale(2.8) translateY(-18px); opacity: 0; filter: blur(10px); }
          to { transform: scale(1) translateY(0); opacity: 1; filter: blur(0); }
        }
        @keyframes sa-fade-up {
          from { transform: translateY(18px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes sa-corner-spark {
          from { transform: scale(0) rotate(-30deg); opacity: 0; }
          45% { transform: scale(1.5) rotate(12deg); opacity: 1; }
          to { transform: scale(1) rotate(0deg); opacity: 0.85; }
        }
        ${particles.map(p => `
          @keyframes sa-particle-fly-${p.id % 6} {
            from { transform: translate(-50%, -50%) scale(0); opacity: 1; }
            55% { opacity: 0.85; }
            to { transform: translate(calc(-50% + var(--px)), calc(-50% + var(--py))) scale(0.2); opacity: 0; }
          }
        `).join('')}
      `}</style>
    </div>
  );
}
